import React, { useState, useMemo } from "react";
import { Card } from "./Deck";
import CardView from "./CardView";
import CardPicker from "./CardPicker";
import {
  findOuts,
  calculateEquity,
  effectiveHandName,
  formatCard,
  OutsResult,
  EquityResult,
} from "./evaluator";
import InfoTooltip from "./InfoTooltip";
import FormulaDisplay, {
  FormulaData,
  RunnerRunnerFormula,
} from "./FormulaDisplay";

const EQUITY_EXPLANATION =
  "Equity is the percentage of the time your hand would beat a completely random opponent hand. It tests every possible 2-card combination an opponent could hold and checks who wins — giving you an exact win rate assuming you have no reads on your opponent.";

type SlotTarget = "hole" | "community";

const COMMUNITY_COUNTS = [0, 3, 4, 5] as const;

const CalculateMode: React.FC = () => {
  const [holeCards, setHoleCards] = useState<Card[]>([]);
  const [communityCards, setCommunityCards] = useState<Card[]>([]);
  const [communitySize, setCommunitySize] = useState<number>(0);
  const [pickerTarget, setPickerTarget] = useState<SlotTarget | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  const allCards = [...holeCards, ...communityCards];

  const holeComplete = holeCards.length === 2;
  const communityComplete = communityCards.length === communitySize;
  const canAnalyze = holeComplete && communityComplete && communitySize > 0;

  // Auto-compute results when all cards are filled
  const outsResult: OutsResult | null = useMemo(() => {
    if (!holeComplete || !communityComplete) return null;
    if (communitySize < 3 || communitySize > 4) return null;
    try {
      return findOuts(holeCards, communityCards);
    } catch {
      return null;
    }
  }, [
    holeCards,
    communityCards,
    holeComplete,
    communityComplete,
    communitySize,
  ]);

  const equityResult: EquityResult | null = useMemo(() => {
    if (!holeComplete || !communityComplete) return null;
    if (communitySize < 4) return null;
    try {
      return calculateEquity(holeCards, communityCards);
    } catch {
      return null;
    }
  }, [
    holeCards,
    communityCards,
    holeComplete,
    communityComplete,
    communitySize,
  ]);

  const currentHandName = useMemo(() => {
    if (!holeComplete || !communityComplete || communitySize < 3) return null;
    try {
      // If we have outs result, use its label (includes board annotation)
      if (outsResult) return outsResult.currentHand.name;
      // For river (5 community), use effectiveHandName
      return effectiveHandName(holeCards, communityCards);
    } catch {
      return null;
    }
  }, [
    holeCards,
    communityCards,
    holeComplete,
    communityComplete,
    communitySize,
    outsResult,
  ]);

  const openPicker = (target: SlotTarget) => {
    setPickerTarget(target);
    setShowPicker(true);
  };

  const handlePick = (card: Card) => {
    if (pickerTarget === "hole" && holeCards.length < 2) {
      const next = [...holeCards, card];
      setHoleCards(next);
      if (next.length === 2) {
        // Auto-advance to community if community size > 0
        if (communitySize > 0 && communityCards.length < communitySize) {
          setPickerTarget("community");
        } else {
          setShowPicker(false);
        }
      }
    } else if (
      pickerTarget === "community" &&
      communityCards.length < communitySize
    ) {
      const next = [...communityCards, card];
      setCommunityCards(next);
      if (next.length >= communitySize) {
        setShowPicker(false);
      }
    }
  };

  const removeHoleCard = (index: number) => {
    setHoleCards((prev) => prev.filter((_, i) => i !== index));
  };

  const removeCommunityCard = (index: number) => {
    setCommunityCards((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReset = () => {
    setHoleCards([]);
    setCommunityCards([]);
    setShowPicker(false);
    setPickerTarget(null);
  };

  const handleCommunityCountChange = (count: number) => {
    setCommunitySize(count);
    // Trim community cards if needed
    if (communityCards.length > count) {
      setCommunityCards(communityCards.slice(0, count));
    }
  };

  return (
    <div className="flex flex-col items-center gap-5">
      {/* Community card count selector */}
      <div>
        <div className="text-gray-400 text-xs uppercase tracking-wider mb-2 text-center">
          Board stage
        </div>
        <div className="flex gap-1.5">
          {COMMUNITY_COUNTS.map((count) => (
            <button
              key={count}
              onClick={() => handleCommunityCountChange(count)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                ${
                  communitySize === count
                    ? "bg-amber-500 text-gray-900"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                }`}
            >
              {count === 0
                ? "Pre-flop"
                : count === 3
                ? "Flop (3)"
                : count === 4
                ? "Turn (4)"
                : "River (5)"}
            </button>
          ))}
        </div>
      </div>

      {/* Hole cards */}
      <div>
        <div className="text-gray-400 text-xs uppercase tracking-wider mb-2 text-center">
          Your Hand
        </div>
        <div className="flex gap-2 justify-center">
          {[0, 1].map((i) => (
            <CardView
              key={i}
              card={holeCards[i]}
              size="lg"
              placeholder={!holeCards[i]}
              onClick={
                holeCards[i]
                  ? () => removeHoleCard(i)
                  : () => openPicker("hole")
              }
            />
          ))}
        </div>
      </div>

      {/* Community cards */}
      {communitySize > 0 && (
        <div>
          <div className="text-gray-400 text-xs uppercase tracking-wider mb-2 text-center">
            Community
          </div>
          <div className="flex gap-2 justify-center">
            {Array.from({ length: communitySize }).map((_, i) => (
              <CardView
                key={i}
                card={communityCards[i]}
                size="lg"
                placeholder={!communityCards[i]}
                onClick={
                  communityCards[i]
                    ? () => removeCommunityCard(i)
                    : () => openPicker("community")
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Card picker */}
      {showPicker && (
        <div className="w-full max-w-sm">
          <CardPicker
            usedCards={allCards}
            onPick={handlePick}
            onClose={() => setShowPicker(false)}
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        {allCards.length > 0 && (
          <button
            onClick={handleReset}
            className="h-10 px-4 bg-gray-700 hover:bg-gray-600 active:scale-95 text-gray-300 text-sm rounded-lg transition-all"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Results */}
      {canAnalyze && (currentHandName || outsResult || equityResult) && (
        <div className="w-full max-w-sm bg-gray-800/80 rounded-xl p-4 border border-gray-700 space-y-3">
          {currentHandName && (
            <div className="text-center">
              <span className="text-gray-400 text-xs">Current hand: </span>
              <span className="text-white font-semibold">
                {currentHandName}
              </span>
            </div>
          )}

          {/* Outs */}
          {outsResult && outsResult.outGroups.length > 0 && (
            <div className="space-y-3 pt-2 border-t border-gray-700">
              <div className="text-gray-400 text-xs">
                {outsResult.totalOuts} out
                {outsResult.totalOuts !== 1 ? "s" : ""} ·{" "}
                {outsResult.cardsToCome} card
                {outsResult.cardsToCome !== 1 ? "s" : ""} to come ·{" "}
                <span className="text-amber-400">
                  {outsResult.hitAnyPercent}%
                </span>{" "}
                to improve
              </div>
              {outsResult.outGroups.map((g) => {
                const formulaData: FormulaData = {
                  outs: g.count,
                  nonOuts: outsResult.remainingCount - g.count,
                  remaining: outsResult.remainingCount,
                  cardsToCome: outsResult.cardsToCome,
                  percent: g.hitPercent,
                };
                return (
                  <div
                    key={g.improvementName}
                    className="space-y-1.5 bg-gray-900/50 rounded-lg p-2.5"
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-white font-medium">
                        {g.improvementName}
                      </span>
                      <span className="text-xs font-semibold text-amber-400">
                        {g.hitPercent}%
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {g.cards.map((c) => (
                        <span
                          key={formatCard(c)}
                          className="text-xs font-mono bg-gray-700 px-1.5 py-0.5 rounded"
                          style={{
                            color:
                              c.suit === "h" || c.suit === "d"
                                ? "#ef4444"
                                : "#e5e7eb",
                          }}
                        >
                          {formatCard(c)}
                        </span>
                      ))}
                      <span className="text-[10px] text-gray-500 self-center ml-1">
                        {g.count} out{g.count !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="pl-1">
                      <FormulaDisplay data={formulaData} />
                    </div>
                  </div>
                );
              })}
              {outsResult.outGroups.length > 1 && (
                <div className="space-y-1.5 pt-2 border-t border-gray-700/50">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-gray-400">
                      Any improvement ({outsResult.totalOuts} outs)
                    </span>
                    <span className="text-xs font-semibold text-amber-400">
                      {outsResult.hitAnyPercent}%
                    </span>
                  </div>
                  <div className="pl-1">
                    <FormulaDisplay
                      data={{
                        outs: outsResult.totalOuts,
                        nonOuts:
                          outsResult.remainingCount - outsResult.totalOuts,
                        remaining: outsResult.remainingCount,
                        cardsToCome: outsResult.cardsToCome,
                        percent: outsResult.hitAnyPercent,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Runner-runner groups */}
              {outsResult.runnerRunnerGroups.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-gray-700/50">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                    Runner-runner (needs both cards)
                  </div>
                  {outsResult.runnerRunnerGroups.map((rr) => (
                    <div
                      key={rr.improvementName}
                      className="space-y-1.5 bg-gray-900/30 rounded-lg p-2.5 border border-gray-700/30"
                    >
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-white font-medium">
                          {rr.improvementName}
                        </span>
                        <span className="text-xs font-semibold text-amber-400/70">
                          {rr.hitPercent}%
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {rr.comboCount} combo{rr.comboCount !== 1 ? "s" : ""}{" "}
                        out of {rr.totalCombos}
                      </div>
                      <div className="pl-1">
                        <RunnerRunnerFormula
                          data={{
                            comboCount: rr.comboCount,
                            totalCombos: rr.totalCombos,
                            remaining: outsResult.remainingCount,
                            percent: rr.hitPercent,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Total including runner-runner */}
              {outsResult.runnerRunnerGroups.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-gray-700/50">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-gray-400 font-medium">
                      Total (outs + runner-runner)
                    </span>
                    <span className="text-xs font-bold text-amber-400">
                      {outsResult.hitAnyIncludingRRPercent}%
                    </span>
                  </div>
                  <div className="pl-1">
                    <RunnerRunnerFormula
                      data={{
                        comboCount: outsResult.totalImprovingCombos,
                        totalCombos: outsResult.totalPossibleCombos,
                        remaining: outsResult.remainingCount,
                        percent: outsResult.hitAnyIncludingRRPercent,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {outsResult &&
            outsResult.outGroups.length === 0 &&
            outsResult.runnerRunnerGroups.length === 0 &&
            communitySize < 5 && (
              <div className="text-gray-500 text-xs text-center pt-2 border-t border-gray-700">
                No improving outs found
              </div>
            )}

          {/* Also show runner-runner even when no single-card outs */}
          {outsResult &&
            outsResult.outGroups.length === 0 &&
            outsResult.runnerRunnerGroups.length > 0 &&
            communitySize < 5 && (
              <div className="space-y-3 pt-2 border-t border-gray-700">
                <div className="text-gray-400 text-xs">No single-card outs</div>
                <div className="space-y-2">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                    Runner-runner (needs both cards)
                  </div>
                  {outsResult.runnerRunnerGroups.map((rr) => (
                    <div
                      key={rr.improvementName}
                      className="space-y-1.5 bg-gray-900/30 rounded-lg p-2.5 border border-gray-700/30"
                    >
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-white font-medium">
                          {rr.improvementName}
                        </span>
                        <span className="text-xs font-semibold text-amber-400/70">
                          {rr.hitPercent}%
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {rr.comboCount} combo{rr.comboCount !== 1 ? "s" : ""}{" "}
                        out of {rr.totalCombos}
                      </div>
                      <div className="pl-1">
                        <RunnerRunnerFormula
                          data={{
                            comboCount: rr.comboCount,
                            totalCombos: rr.totalCombos,
                            remaining: outsResult.remainingCount,
                            percent: rr.hitPercent,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {/* Total (runner-runner only, no single-card outs) */}
                {outsResult.runnerRunnerGroups.length > 1 && (
                  <div className="space-y-1.5 pt-2 border-t border-gray-700/50">
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-gray-400 font-medium">
                        Total (all runner-runner)
                      </span>
                      <span className="text-xs font-bold text-amber-400">
                        {outsResult.hitAnyIncludingRRPercent}%
                      </span>
                    </div>
                    <div className="pl-1">
                      <RunnerRunnerFormula
                        data={{
                          comboCount: outsResult.totalImprovingCombos,
                          totalCombos: outsResult.totalPossibleCombos,
                          remaining: outsResult.remainingCount,
                          percent: outsResult.hitAnyIncludingRRPercent,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Equity */}
          {equityResult && (
            <div className="space-y-2 pt-2 border-t border-gray-700">
              <div className="text-gray-400 text-xs text-center inline-flex items-center justify-center w-full">
                Equity vs. random hand
                <InfoTooltip text={EQUITY_EXPLANATION} />
              </div>
              <div className="flex justify-center gap-6">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-400">
                    {equityResult.winPercent}%
                  </div>
                  <div className="text-xs text-gray-500">Win</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-400">
                    {equityResult.tiePercent}%
                  </div>
                  <div className="text-xs text-gray-500">Tie</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-red-400">
                    {equityResult.losePercent}%
                  </div>
                  <div className="text-xs text-gray-500">Lose</div>
                </div>
              </div>
              <div className="text-gray-600 text-xs text-center">
                {equityResult.totalMatchups.toLocaleString()} matchups
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pre-flop message */}
      {communitySize === 0 && holeComplete && (
        <div className="text-gray-500 text-sm text-center px-4">
          Select a board stage to see outs and equity.
        </div>
      )}
    </div>
  );
};

export default CalculateMode;
